import AssortmentService from '@/services/assortment.service'
import Filter from '@/models/assortmentfilter.model'
import Utils from '@/utils'
import _ from 'lodash'

const state = {
    curves: [],
    last_entry_tracks: [],
    tc_tracks: [],
    curvesLoading: false,
    curvesPromise: null,
    curvesMiniDCLoading: false,
    curvesMiniDCPromise: null,

    curvesHistogram: [],
    curvesHistogramLoading: false,
    curvesHistogramPromise: null,

    curveDetails: {
        results: [],
        count: 0,
        next: null,
        previous: null
    },
    curveDetailsLoading: false,
    curveDetailsPromise: null,

    filters: [new Filter()]
}

const getters = {
    getCurvesPromise(state) {
        if (!state.curvesLoading) return null
        return state.curvesPromise
    },
    getCurvesMiniDCPromise(state) {
        if (!state.curvesMiniDCLoading) return null
        return state.curvesMiniDCPromise
    },
    getCurveDetailsPromise(state) {
        if (!state.curveDetailsLoading) return null
        return state.curveDetailsPromise
    }
}

const actions = {
    read: async ({ commit, getters }, args) => {
        let readPromise = getters.getCurvesPromise
        if (readPromise) return readPromise
        try {
            let data = AssortmentService.read(args)

            commit('reading', data)
            data.then(value => {
                commit('SET_CURVES', value.data)
                commit('SET_LAST_ENTRY_TRACKS', value.data)
                commit('SET_TC_TRACKS', value.data)
            })

            return null
        } catch (e) {
            commit('readFail')
            if (e && e.response) return e.response.status
            else return 500
        }
    },
    filterMiniDCs: async ({ commit, state, getters }, args) => {
        let readMiniCD = getters.getCurvesMiniDCPromise
        if (readMiniCD) return readMiniCD

        try {
            let data = AssortmentService.read(args)
            commit('READ_CURVES', data)

            data.then(value => {
                commit('SET_CURVES', value.data)
            })
            return null
        } catch (e) {
            commit('readFail')
            if (e && e.response) return e.response.status
            else return 500
        }
    },

    readHistogram: async ({ commit, getters }, args) => {
        let readPromise = getters.curvesHistogramPromise
        if (readPromise) return readPromise

        try {
            let data = AssortmentService.readHistogram(args)
            commit('readingHistogram', data)
            data = await data
            commit('readHistogramSuccess', data.data)
            return null
        } catch (e) {
            commit('readHistogramFail')
            if (e && e.response) return e.response.status
            else return 500
        }
    },

    readDetails: async ({ commit, getters }, { disableLoading = false, ...args }) => {
        let readPromise = getters.getCurveDetailsPromise
        if (readPromise) return readPromise
        if (!args.page) args.page = 1
        if (!args.limit) args.limit = 20
        try {
            let data = AssortmentService.readDetails(args)
            if (!disableLoading) {
                commit('readingDetails', data)
            }
            data = await data
            commit('readDetailsSuccess', data.data)
            return null
        } catch (e) {
            commit('readDetailsFail')
            if (e && e.response) return e.response.status
            else return 500
        }
    },

    /** FILTER MANAGEMENT **/
    createFilter: async ({ commit }, args) => {
        try {
            let result = await AssortmentService.createFilter(args)
            commit('createFilterSuccess', result.data)
            return null
        } catch (e) {
            if (e && e.response) return e.response.status
            else return 500
        }
    },

    readFilters: async ({ commit }, args) => {
        try {
            let data = await AssortmentService.readFilters(args)
            commit('readFiltersSuccess', data.data)
            return null
        } catch (e) {
            if (e && e.response) return e.response.status
            else return 500
        }
    },

    updateFilter: async ({ commit }, args) => {
        try {
            await AssortmentService.updateFilter(args.id, args)
            commit('updateFilterSuccess')
            return null
        } catch (e) {
            if (e && e.response) return e.response.status
            else return 500
        }
    },

    deleteFilter: async ({ commit }, args) => {
        try {
            await AssortmentService.deleteFilter(args.id)
            commit('deleteFilterSuccess', args)
            return null
        } catch (e) {
            if (e && e.response) return e.response.status
            else return 500
        }
    },
    likeFilter: async ({ commit }, args) => {
        try {
            await AssortmentService.likeFilter(args)
            commit('likeFilterSuccess', args)
            return null
        } catch (e) {
            if (e && e.response) return e.response.status
            else return 500
        }
    },
    unlikeFilter: async ({ commit }, args) => {
        try {
            await AssortmentService.unlikeFilter(args)
            commit('unlikeFilterSuccess', args)
            return null
        } catch (e) {
            if (e && e.response) return e.response.status
            else return 500
        }
    },
    updateLastSeenFilter: async ({ commit }, args) => {
        try {
            await AssortmentService.updateLastSeen(args)
            commit('updateLastSeenFilterSuccess', args)
            return null
        } catch (e) {
            if (e && e.response) return e.response.status
            else return 500
        }
    },
    downloadFile: async ({ commit, getters }, args) => {
        try {
            const data = await AssortmentService.downloadFile(args)

            const url = window.URL.createObjectURL(new Blob([data.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', 'assortment.xls')
            document.body.appendChild(link)
            link.click()

            commit('downloadFileSuccess', data.data)
            return null
        } catch (e) {
            if (e && e.response) return e.response.status
            else return 500
        }
    }
}

const mutations = {
    reading(state, data) {
        state.curvesPromise = data

        state.curvesLoading = true
    },
    READ_CURVES(state, data) {
        state.curvesMiniCDPromise = data
        state.curvesMiniCDLoading = true
    },
    SET_CURVES(state, { curves }) {
        state.curves = curves
        state.curvesLoading = false
        state.curvesMiniCDLoading = false
    },
    SET_LAST_ENTRY_TRACKS(state, { last_entry_tracks }) {
        state.last_entry_tracks = last_entry_tracks
    },
    SET_TC_TRACKS(state, { tc_tracks }) {
        state.tc_tracks = tc_tracks
    },
    readSuccess(state, data) {
        state.curves = data.curves
        state.last_entry_tracks = data.last_entry_tracks
        state.tc_tracks = data.tc_tracks
        state.curvesLoading = false
    },
    readFail(state) {
        state.curvesLoading = false
        state.curvesPromise = null
    },

    readingHistogram(state, data) {
        state.curvesHistogramPromise = data
        state.curvesHistogramLoading = true
    },
    readHistogramSuccess(state, data) {
        state.curvesHistogram = data
        state.curvesHistogramLoading = false
    },
    readHistogramFail(state) {
        state.curvesHistogramLoading = false
        state.curvesHistogramPromise = null
    },

    readingDetails(state, data) {
        state.curveDetailsPromise = data
        state.curveDetailsLoading = true
    },
    async readDetailsSuccess(state, data) {
        await Utils.datetimeConverter(data.results)
        state.curveDetails = data
        state.curveDetailsLoading = false
    },
    readDetailsFail(state) {
        state.curveDetailsPromise = null
        state.curveDetailsLoading = false
    },

    createFilterSuccess(state, data) {
        var filters = _.cloneDeep(state.filters)
        filters.push(data)

        var filter_filtered = filters
            .filter(elem => elem.id != null)
            .map(a => {
                if (a.id === data) {
                    a.datetime_last_seen = new Date().toISOString()
                }
                return a
            })
            .sort(
                (a, b) =>
                    new Date(b.datetime_last_seen).getTime() -
                    new Date(a.datetime_last_seen).getTime()
            )
        filter_filtered = filter_filtered.concat(
            filters.filter(elem => elem.id == null)
        )

        state.filters = filter_filtered
    },
    readFiltersSuccess(state, data) {
        state.filters = data //.sort((a, b) => (new Date(a.datetime_updated)).getTime() - (new Date(b.datetime_updated)).getTime())
        state.filters.push(new Filter()) // add default filter to the end
    },
    updateFilterSuccess(state) { },

    deleteFilterSuccess(state, args) {
        state.filters = state.filters.filter(elem => elem.id != args.id)
    },

    likeFilterSuccess(state, data) {
        state.filters = state.filters.map(a => {
            if (a.id === data) {
                a.liked = true
            }
            return a
        })
    },
    unlikeFilterSuccess(state, data) {
        state.filters = state.filters.map(a => {
            if (a.id === data) {
                a.liked = false
            }
            return a
        })
    },

    updateLastSeenFilterSuccess(state, data) {
        state.filters = state.filters
            .map(a => {
                if (a.id === data) {
                    a.datetime_last_seen = new Date().toISOString()
                }
                return a
            })
            .sort(
                (a, b) =>
                    new Date(b.datetime_last_seen).getTime() -
                    new Date(a.datetime_last_seen).getTime()
            )
    },

    downloadFileSuccess(state) {
        // success
    }
}

const assortment = {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}

export default assortment
export { assortment }
